<template>
  <div id="app">
    <router-view />
    <div v-if="showSessionExpired" class="session-backdrop">
      <div class="session-modal" role="dialog" aria-modal="true">
        <h3>Sesión expirada</h3>
        <p>Tu sesión ha caducado. Por favor, vuelve a iniciar sesión.</p>
        <button class="primary" @click="redirectToLogin">Aceptar</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "App",
  data() {
    return {
      showSessionExpired: false,
    };
  },
  mounted() {
    window.addEventListener("session-expired", this.onSessionExpired);
  },
  beforeUnmount() {
    window.removeEventListener("session-expired", this.onSessionExpired);
  },
  methods: {
    onSessionExpired() {
      if (this.$route?.name === "login") return;
      this.showSessionExpired = true;
    },
    redirectToLogin() {
      this.showSessionExpired = false;
      this.$router.push("/login");
    },
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;600&display=swap");

:root {
  --color-bg-primary: #0b1220;
  --color-bg-secondary: #101a32;
  --color-card: #121a2d;
  --color-card-elevated: #16223d;
  --color-accent: #3bb7ff;
  --color-danger: #f87171;
  --color-success: #34d399;
  --color-warning: #fbbf24;

  --bg: var(--color-bg-primary);
  --bg-soft: var(--color-bg-secondary);
  --kanban-bg: #0c1426;
  --column-bg: #0f1b34;
  --card-bg: #131e35;
  --panel: var(--color-card);
  --panel-2: #14213b;
  --panel-3: #0e172b;
  --text: #e2e8f0;
  --muted: #9aa7c1;
  --accent: var(--color-accent);
  --accent-2: #f59e0b;
  --accent-3: var(--color-success);
  --danger: var(--color-danger);
  --success: var(--color-success);
  --border: rgba(148, 163, 184, 0.18);
  --shadow: 0 18px 40px rgba(5, 10, 25, 0.4);

  --font-sans: "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  --font-mono: "IBM Plex Mono", ui-monospace, SFMono-Regular, SFMono-Regular, monospace;
  --font-size-h1: 32px;
  --font-size-h2: 20px;
  --font-size-body: 14px;
  --safe-top: env(safe-area-inset-top);
  --safe-right: env(safe-area-inset-right);
  --safe-bottom: env(safe-area-inset-bottom);
  --safe-left: env(safe-area-inset-left);
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-body);
  line-height: 1.6;
  color: var(--text);
  background: radial-gradient(800px 420px at 15% -10%, rgba(59, 183, 255, 0.08), transparent 60%),
    var(--bg);
  min-height: 100vh;
  padding: var(--safe-top) var(--safe-right) var(--safe-bottom) var(--safe-left);
}

a {
  color: inherit;
  text-decoration: none;
}

button {
  font-family: inherit;
}

#app {
  min-height: 100vh;
}

@media (max-width: 900px) and (orientation: landscape) {
  body {
    display: flex;
    justify-content: center;
  }

  #app {
    width: min(100vw, 100vh);
    max-width: 520px;
  }
}

.session-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(3, 7, 18, 0.7);
  display: grid;
  place-items: center;
  z-index: 100;
  padding: 24px;
}

.session-modal {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 24px;
  width: min(420px, 92vw);
  box-shadow: var(--shadow);
  display: grid;
  gap: 12px;
  text-align: center;
}

.session-modal h3 {
  margin: 0;
  font-size: 20px;
}

.session-modal p {
  margin: 0;
  color: var(--muted);
}

* {
  scrollbar-width: none;
}

*::-webkit-scrollbar {
  width: 0;
  height: 0;
}

@supports (-webkit-touch-callout: none) {
  .modal-body input[type="date"],
  .modal-body input[type="datetime-local"],
  .modal-body input[type="time"] {
    display: block;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    -webkit-appearance: none;
    appearance: none;
  }
}

.skeleton {
  position: relative;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.12);
  border-radius: 12px;
}

.skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.08),
    transparent
  );
  animation: shimmer 1.4s infinite;
}

.skeleton-line {
  height: 12px;
  border-radius: 999px;
}

.skeleton-line.lg {
  height: 18px;
}

.skeleton-line.xl {
  height: 28px;
}

.skeleton-block {
  border-radius: 12px;
}

.skeleton-circle {
  border-radius: 50%;
}

.skeleton-stack {
  display: grid;
  gap: 12px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
